/**
 * CC Marketing: Blog Post Editor
 * Edit/Preview toggle with TipTap rich text editor, custom image controls,
 * and pixel-exact preview matching coachcedar.com blog layout.
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Youtube from '@tiptap/extension-youtube';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import DOMPurify from 'dompurify';
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  Heading2, Heading3, Heading4, List, ListOrdered,
  Quote, Minus, Link2, Image as ImageIcon, Youtube as YoutubeIcon,
  AlignLeft, AlignCenter, AlignRight,
  Save, Send, CalendarClock, Eye, Edit3, ArrowLeft, History,
  Upload, X, Settings, Type,
} from 'lucide-react';
import { AdminBlogService, BlogPost, BlogMedia, BlogCategory, BlogAuthor } from '../services/adminBlogService';
import { BlogImage } from '../components/blog/BlogImageExtension';
import { BlogPreview } from '../components/blog/BlogPreview';
import { PublishChecklist, PostData } from '../components/blog/PublishChecklist';
import { SocialPreviews, SocialPreviewData } from '../components/blog/SocialPreviews';
import { InfoTooltip, SOCIAL_FIELD_TIPS } from '../components/blog/InfoTooltip';

type EditorMode = 'edit' | 'preview';

export const AdminBlogEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Post state
  const [post, setPost] = useState<BlogPost | null>(null);
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [excerpt, setExcerpt] = useState('');
  const [author, setAuthor] = useState('Coach Cedar');
  const [authorId, setAuthorId] = useState<string | null>(null);
  const [category, setCategory] = useState('Product');
  const [tags, setTags] = useState('');
  const [heroImageUrl, setHeroImageUrl] = useState('');
  const [seoTitle, setSeoTitle] = useState('');
  const [seoDescription, setSeoDescription] = useState('');
  const [publishAt, setPublishAt] = useState('');
  const [status, setStatus] = useState('draft');

  // Social sharing state
  const [socialTitle, setSocialTitle] = useState('');
  const [socialDescription, setSocialDescription] = useState('');
  const [socialImageUrl, setSocialImageUrl] = useState('');
  const [socialImageAlt, setSocialImageAlt] = useState('');
  const [twitterCardType, setTwitterCardType] = useState('summary_large_image');
  const [twitterTitle, setTwitterTitle] = useState('');
  const [twitterDescription, setTwitterDescription] = useState('');
  const [twitterImageUrl, setTwitterImageUrl] = useState('');
  const [twitterImageAlt, setTwitterImageAlt] = useState('');
  const [pinterestImageUrl, setPinterestImageUrl] = useState('');
  const [canonicalUrlOverride, setCanonicalUrlOverride] = useState('');
  const [noindex, setNoindex] = useState(false);

  // Pending content to hydrate into editor once it's ready
  const pendingContentRef = useRef<{ json?: any; html?: string } | null>(null);

  // UI state
  const [mode, setMode] = useState<EditorMode>('edit');
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showMediaLibrary, setShowMediaLibrary] = useState(false);
  const [showSettings, setShowSettings] = useState(true);
  const [mediaItems, setMediaItems] = useState<BlogMedia[]>([]);
  const [uploading, setUploading] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [addingCategory, setAddingCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [addingCategoryError, setAddingCategoryError] = useState<string | null>(null);
  const [authors, setAuthors] = useState<BlogAuthor[]>([]);
  const [showPublishChecklist, setShowPublishChecklist] = useState(false);

  // Fetch categories and authors on mount
  useEffect(() => {
    AdminBlogService.listCategories()
      .then(setCategories)
      .catch(err => console.error('Failed to load categories:', err));
    AdminBlogService.listAuthors()
      .then(setAuthors)
      .catch(err => console.error('Failed to load authors:', err));
  }, []);

  // TipTap editor with custom BlogImage extension
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3, 4] },
      }),
      BlogImage,
      Youtube.configure({
        width: 640,
        height: 360,
        HTMLAttributes: { class: 'blog-editor-youtube' },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' },
      }),
      Placeholder.configure({
        placeholder: 'Start writing your blog post...',
      }),
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      TextStyle,
      Color,
    ],
    content: '',
    editorProps: {
      attributes: {
        class: 'blog-editor-content',
        style: 'min-height: 500px; outline: none; padding: 24px; font-size: 16px; line-height: 1.8;',
      },
    },
    onCreate: ({ editor: ed }) => {
      if (pendingContentRef.current) {
        const { json, html } = pendingContentRef.current;
        if (json) ed.commands.setContent(json);
        else if (html) ed.commands.setContent(html);
        pendingContentRef.current = null;
      }
    },
  });

  // Load existing post. useEditor can destroy its editor and hand out a new one while the
  // page mounts, which re-runs this effect; the superseded run must not touch the editor it
  // captured, since a destroyed TipTap editor throws on `commands`.
  useEffect(() => {
    if (isNew || !id) return;
    let cancelled = false;
    (async () => {
      try {
        const p = await AdminBlogService.getPost(id);
        if (cancelled) return;
        setPost(p);
        setTitle(p.title);
        setSlug(p.slug);
        setSlugManuallyEdited(true);
        setExcerpt(p.excerpt || '');
        setAuthor(p.author);
        setAuthorId(p.author_id || null);
        setCategory(p.category);
        setTags((p.tags || []).join(', '));
        setHeroImageUrl(p.hero_image_url || '');
        setSeoTitle(p.seo_title || '');
        setSeoDescription(p.seo_description || '');
        setStatus(p.status);
        // Social sharing fields
        setSocialTitle(p.social_title || '');
        setSocialDescription(p.social_description || '');
        setSocialImageUrl(p.social_image_url || '');
        setSocialImageAlt(p.social_image_alt || '');
        setTwitterCardType(p.twitter_card_type || 'summary_large_image');
        setTwitterTitle(p.twitter_title || '');
        setTwitterDescription(p.twitter_description || '');
        setTwitterImageUrl(p.twitter_image_url || '');
        setTwitterImageAlt(p.twitter_image_alt || '');
        setPinterestImageUrl(p.pinterest_image_url || '');
        setCanonicalUrlOverride(p.canonical_url_override || '');
        setNoindex(p.noindex || false);
        if (p.publish_at) setPublishAt(new Date(p.publish_at).toISOString().slice(0, 16));

        // asyncpg returns JSONB columns as raw JSON strings in this app
        // (no set_type_codec configured). Parse to object so TipTap gets
        // Prosemirror JSON, not a string it would render as plain text.
        let contentJson = p.content_json;
        if (typeof contentJson === 'string') {
          try { contentJson = JSON.parse(contentJson); } catch { contentJson = null; }
        }
        const content = contentJson || p.content_html || '';
        if (editor && !editor.isDestroyed) {
          editor.commands.setContent(content);
        } else {
          pendingContentRef.current = contentJson
            ? { json: contentJson }
            : { html: p.content_html || '' };
        }
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to load post:', err);
        setError('Failed to load post. Please try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, isNew, editor]);

  // Auto-generate slug from title (only for new posts or when slug hasn't been manually edited)
  useEffect(() => {
    if (!slugManuallyEdited) {
      const autoSlug = title.toLowerCase().trim()
        .replace(/[^\w\s-]/g, '').replace(/[\s_]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
      setSlug(autoSlug);
    }
  }, [title, slugManuallyEdited]);

  const getPostData = useCallback(() => ({
    title,
    slug,
    excerpt,
    content_html: editor?.getHTML() || '',
    content_json: editor?.getJSON() || null,
    author,
    author_id: authorId || null,
    category,
    tags: tags.split(',').map(t => t.trim()).filter(Boolean),
    hero_image_url: heroImageUrl || null,
    seo_title: seoTitle || null,
    seo_description: seoDescription || null,
    // Social sharing fields
    social_title: socialTitle || null,
    social_description: socialDescription || null,
    social_image_url: socialImageUrl || null,
    social_image_alt: socialImageAlt || null,
    twitter_card_type: twitterCardType || 'summary_large_image',
    twitter_title: twitterTitle || null,
    twitter_description: twitterDescription || null,
    twitter_image_url: twitterImageUrl || null,
    twitter_image_alt: twitterImageAlt || null,
    pinterest_image_url: pinterestImageUrl || null,
    canonical_url_override: canonicalUrlOverride || null,
    noindex,
  }), [title, slug, excerpt, editor, author, authorId, category, tags, heroImageUrl, seoTitle, seoDescription,
    socialTitle, socialDescription, socialImageUrl, socialImageAlt, twitterCardType,
    twitterTitle, twitterDescription, twitterImageUrl, twitterImageAlt, pinterestImageUrl, canonicalUrlOverride, noindex]);

  const handleSave = async (): Promise<BlogPost | null> => {
    setSaving(true);
    setSaveError(null);
    try {
      const data = getPostData();
      let saved: BlogPost;
      if (post) {
        saved = await AdminBlogService.updatePost(post.id, data);
      } else {
        saved = await AdminBlogService.createPost(data);
        navigate(`/admin/marketing/blog/${saved.id}`, { replace: true });
      }
      setPost(saved);
      setStatus(saved.status);
      setLastSaved(new Date());
      return saved;
    } catch (err: any) {
      const msg = err?.message || 'Save failed. Check your connection and try again.';
      setSaveError(msg);
      console.error('Failed to save:', err);
      return null;
    } finally {
      setSaving(false);
    }
  };

  // Build PostData snapshot for the publish checklist validation
  const getChecklistPostData = useCallback((): PostData => ({
    title,
    slug,
    excerpt,
    contentHtml: editor?.getHTML() || '',
    seoTitle: seoTitle,
    seoDescription: seoDescription,
    author,
    authorId: authorId,
    category,
    heroImageUrl: heroImageUrl,
    tags: tags.split(',').map(t => t.trim()).filter(Boolean),
    socialTitle: socialTitle,
    socialDescription: socialDescription,
    socialImageUrl: socialImageUrl,
    socialImageAlt: socialImageAlt,
    canonicalUrlOverride: canonicalUrlOverride,
  }), [title, slug, excerpt, editor, seoTitle, seoDescription, author, authorId, category,
    tags, heroImageUrl, socialTitle, socialDescription, socialImageUrl, socialImageAlt, canonicalUrlOverride]);

  // Open the publish validation checklist instead of publishing directly
  const handlePublishGate = () => {
    setShowPublishChecklist(true);
  };

  // Actual publish action — called from checklist after validation passes
  const handlePublish = async () => {
    setShowPublishChecklist(false);
    const saved = await handleSave();
    if (!saved) return; // save failed — error already shown
    // Already published: save is enough (avoids resetting published_at)
    if (saved.status === 'published') return;
    try {
      const updated = await AdminBlogService.publishPost(saved.id);
      setPost(updated);
      setStatus('published');
    } catch (err: any) {
      setSaveError(err?.message || 'Publish failed.');
      console.error('Failed to publish:', err);
    }
  };

  const handleSchedule = async () => {
    if (!publishAt) return;
    const saved = await handleSave();
    if (!saved) return;
    try {
      const updated = await AdminBlogService.schedulePost(saved.id, new Date(publishAt).toISOString());
      setPost(updated);
      setStatus('scheduled');
    } catch (err: any) {
      setSaveError(err?.message || 'Schedule failed.');
      console.error('Failed to schedule:', err);
    }
  };

  const handleImageUpload = async (file: File) => {
    setUploading(true);
    try {
      const media = await AdminBlogService.uploadMedia(file);
      editor?.commands.setBlogImage({ src: media.url, alt: media.alt_text || '' });
    } catch (err) {
      console.error('Failed to upload image:', err);
    } finally {
      setUploading(false);
    }
  };

  const handleHeroUpload = async (file: File) => {
    setUploading(true);
    try {
      const media = await AdminBlogService.uploadMedia(file);
      setHeroImageUrl(media.url);
    } catch (err) {
      console.error('Failed to upload hero:', err);
    } finally {
      setUploading(false);
    }
  };

  const handleSocialImageUpload = async (file: File, setter: (url: string) => void) => {
    setUploading(true);
    try {
      const media = await AdminBlogService.uploadMedia(file);
      setter(media.url);
    } catch (err) {
      console.error('Failed to upload social image:', err);
    } finally {
      setUploading(false);
    }
  };

  const handleInsertLink = () => {
    const url = window.prompt('Enter URL:');
    if (url) editor?.chain().focus().setLink({ href: url }).run();
  };

  const handleInsertYoutube = () => {
    const url = window.prompt('Enter YouTube URL:');
    if (url) editor?.commands.setYoutubeVideo({ src: url });
  };

  const handleInsertImage = () => {
    fileInputRef.current?.click();
  };

  const handleMediaLibrarySelect = (media: BlogMedia) => {
    editor?.commands.setBlogImage({ src: media.url, alt: media.alt_text || '' });
    setShowMediaLibrary(false);
  };

  const openMediaLibrary = async () => {
    try {
      const items = await AdminBlogService.listMedia();
      setMediaItems(items);
    } catch (err) {
      console.error('Failed to load media:', err);
    }
    setShowMediaLibrary(true);
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '48px', color: '#9ca3af' }}>Loading post...</div>;
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '48px' }}>
        <p style={{ color: '#dc2626', marginBottom: '12px' }}>{error}</p>
        <button
          onClick={() => navigate('/admin/marketing/blog')}
          style={{ padding: '8px 16px', fontSize: '13px', color: '#6b7280', background: 'none', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer' }}
        >
          Back to Blog
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Top bar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button
            onClick={() => navigate('/admin/marketing/blog')}
            style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', fontSize: '13px', color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer' }}
          >
            <ArrowLeft size={16} /> Back
          </button>

          {/* Edit / Preview toggle */}
          <div style={{ display: 'flex', background: '#f3f4f6', borderRadius: '8px', padding: '2px' }}>
            <button
              onClick={() => setMode('edit')}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 14px', fontSize: '13px', fontWeight: 500,
                borderRadius: '6px', border: 'none', cursor: 'pointer',
                background: mode === 'edit' ? 'white' : 'transparent',
                color: mode === 'edit' ? '#111827' : '#6b7280',
                boxShadow: mode === 'edit' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
              }}
            >
              <Edit3 size={14} /> Edit
            </button>
            <button
              onClick={() => setMode('preview')}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 14px', fontSize: '13px', fontWeight: 500,
                borderRadius: '6px', border: 'none', cursor: 'pointer',
                background: mode === 'preview' ? 'white' : 'transparent',
                color: mode === 'preview' ? '#111827' : '#6b7280',
                boxShadow: mode === 'preview' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
              }}
            >
              <Eye size={14} /> Preview
            </button>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {saveError && (
            <span style={{ fontSize: '11px', color: '#dc2626', fontWeight: 500 }}>
              ⚠ {saveError}
            </span>
          )}
          {!saveError && lastSaved && (
            <span style={{ fontSize: '11px', color: '#9ca3af' }}>
              Saved {lastSaved.toLocaleTimeString()}
            </span>
          )}
          <span style={{
            fontSize: '11px', fontWeight: 500, padding: '2px 8px', borderRadius: '4px',
            background: status === 'published' ? '#f0fdf4' : status === 'scheduled' ? '#eff6ff' : '#fefce8',
            color: status === 'published' ? '#166534' : status === 'scheduled' ? '#1e40af' : '#854d0e',
          }}>
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </span>

          {/* Save / Publish buttons */}
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '6px 14px', fontSize: '13px', fontWeight: 500,
              color: '#374151', background: 'white', border: '1px solid #d1d5db',
              borderRadius: '6px', cursor: 'pointer',
            }}
          >
            <Save size={14} /> {saving ? 'Saving...' : 'Save'}
          </button>
          <button
            onClick={handlePublishGate}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '6px 14px', fontSize: '13px', fontWeight: 600,
              color: 'white', background: '#2E5E34', border: 'none',
              borderRadius: '6px', cursor: 'pointer',
            }}
          >
            <Send size={14} /> {status === 'published' ? 'Update' : 'Publish'}
          </button>
        </div>
      </div>

      {/* ══ EDIT MODE ══ */}
      {mode === 'edit' && (
        <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start' }}>
          {/* Left: Editor */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Title */}
            <input
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="Post title..."
              style={{
                width: '100%', padding: '12px 0', fontSize: '28px', fontWeight: 700,
                border: 'none', borderBottom: '1px solid #e5e7eb', outline: 'none',
                marginBottom: '12px', color: '#111827',
              }}
            />

            {/* Toolbar */}
            <div style={{
              display: 'flex', flexWrap: 'wrap', gap: '2px', padding: '8px',
              background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px 8px 0 0',
              borderBottom: 'none',
            }}>
              <ToolbarButton onClick={() => editor?.chain().focus().toggleBold().run()} active={editor?.isActive('bold')} icon={<Bold size={16} />} title="Bold" />
              <ToolbarButton onClick={() => editor?.chain().focus().toggleItalic().run()} active={editor?.isActive('italic')} icon={<Italic size={16} />} title="Italic" />
              <ToolbarButton onClick={() => editor?.chain().focus().toggleUnderline().run()} active={editor?.isActive('underline')} icon={<UnderlineIcon size={16} />} title="Underline" />
              <ToolbarButton onClick={() => editor?.chain().focus().toggleStrike().run()} active={editor?.isActive('strike')} icon={<Strikethrough size={16} />} title="Strikethrough" />
              <ToolbarSep />
              <ToolbarButton onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()} active={editor?.isActive('heading', { level: 2 })} icon={<Heading2 size={16} />} title="Heading 2" />
              <ToolbarButton onClick={() => editor?.chain().focus().toggleHeading({ level: 3 }).run()} active={editor?.isActive('heading', { level: 3 })} icon={<Heading3 size={16} />} title="Heading 3" />
              <ToolbarButton onClick={() => editor?.chain().focus().toggleHeading({ level: 4 }).run()} active={editor?.isActive('heading', { level: 4 })} icon={<Heading4 size={16} />} title="Heading 4" />
              <ToolbarSep />
              <ToolbarButton onClick={() => editor?.chain().focus().toggleBulletList().run()} active={editor?.isActive('bulletList')} icon={<List size={16} />} title="Bullet List" />
              <ToolbarButton onClick={() => editor?.chain().focus().toggleOrderedList().run()} active={editor?.isActive('orderedList')} icon={<ListOrdered size={16} />} title="Numbered List" />
              <ToolbarButton onClick={() => editor?.chain().focus().toggleBlockquote().run()} active={editor?.isActive('blockquote')} icon={<Quote size={16} />} title="Blockquote" />
              <ToolbarButton onClick={() => editor?.chain().focus().setHorizontalRule().run()} icon={<Minus size={16} />} title="Divider" />
              <ToolbarSep />
              <ToolbarButton onClick={() => editor?.chain().focus().setTextAlign('left').run()} active={editor?.isActive({ textAlign: 'left' })} icon={<AlignLeft size={16} />} title="Align Left" />
              <ToolbarButton onClick={() => editor?.chain().focus().setTextAlign('center').run()} active={editor?.isActive({ textAlign: 'center' })} icon={<AlignCenter size={16} />} title="Align Center" />
              <ToolbarButton onClick={() => editor?.chain().focus().setTextAlign('right').run()} active={editor?.isActive({ textAlign: 'right' })} icon={<AlignRight size={16} />} title="Align Right" />
              <ToolbarSep />
              <ToolbarButton onClick={handleInsertLink} icon={<Link2 size={16} />} title="Insert Link" />
              <ToolbarButton onClick={handleInsertImage} icon={<ImageIcon size={16} />} title="Upload Image" />
              <ToolbarButton onClick={openMediaLibrary} icon={<Upload size={16} />} title="Media Library" />
              <ToolbarButton onClick={handleInsertYoutube} icon={<YoutubeIcon size={16} />} title="YouTube Video" />
            </div>

            {/* Editor */}
            <div style={{
              border: '1px solid #e5e7eb', borderRadius: '0 0 8px 8px',
              background: 'white', minHeight: '500px',
            }}>
              <EditorContent editor={editor} />
            </div>

            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={e => {
                const file = e.target.files?.[0];
                if (file) handleImageUpload(file);
                e.target.value = '';
              }}
            />

            {uploading && (
              <div style={{ padding: '8px 12px', fontSize: '12px', color: '#6b7280', background: '#f9fafb', borderRadius: '6px', marginTop: '8px' }}>
                Uploading...
              </div>
            )}

            {/* Live content stats bar */}
            <ContentStatsBar html={editor?.getHTML() || ''} />
          </div>

          {/* Right: Settings Panel (collapsible) */}
          <div style={{ width: showSettings ? '340px' : '40px', flexShrink: 0, transition: 'width 0.2s' }}>
            {!showSettings ? (
              <button
                onClick={() => setShowSettings(true)}
                style={{
                  padding: '8px', background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
                title="Show settings"
              >
                <Settings size={16} style={{ color: '#6b7280' }} />
              </button>
            ) : (
              <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <h3 style={{ fontSize: '14px', fontWeight: 600, color: '#111827', margin: 0 }}>Post Settings</h3>
                  <button onClick={() => setShowSettings(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af' }}>
                    <X size={16} />
                  </button>
                </div>

                {/* Schedule */}
                <div style={{ marginBottom: '16px', padding: '12px', background: '#f9fafb', borderRadius: '6px' }}>
                  <label style={{ fontSize: '12px', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '6px' }}>
                    <CalendarClock size={12} style={{ display: 'inline', marginRight: '4px' }} />
                    Schedule Publish
                  </label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      type="datetime-local"
                      value={publishAt}
                      min={new Date().toISOString().slice(0, 16)}
                      onChange={e => setPublishAt(e.target.value)}
                      style={{ flex: 1, padding: '6px 8px', fontSize: '12px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                    />
                    <button
                      onClick={handleSchedule}
                      disabled={!publishAt}
                      style={{
                        padding: '6px 12px', fontSize: '12px', fontWeight: 500,
                        color: publishAt ? '#1e40af' : '#9ca3af',
                        background: publishAt ? '#eff6ff' : '#f3f4f6',
                        border: `1px solid ${publishAt ? '#bfdbfe' : '#e5e7eb'}`,
                        borderRadius: '4px', cursor: publishAt ? 'pointer' : 'default',
                      }}
                    >
                      Schedule
                    </button>
                  </div>
                </div>

                <Divider />

                {/* Metadata */}
                <FieldGroup label="Slug">
                  <input type="text" value={slug} onChange={e => { setSlug(e.target.value); setSlugManuallyEdited(true); }} style={inputStyle} />
                </FieldGroup>

                <FieldGroup label="Category">
                  <select value={category} onChange={e => setCategory(e.target.value)} style={inputStyle}>
                    {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                    {categories.length === 0 && <option value="Product">Product</option>}
                  </select>
                </FieldGroup>

                <FieldGroup label="Author">
                  {authors.length > 0 ? (
                    <select
                      value={authorId || ''}
                      onChange={e => {
                        const selected = authors.find(a => a.id === e.target.value);
                        if (selected) {
                          setAuthorId(selected.id);
                          setAuthor(selected.name);
                        } else {
                          setAuthorId(null);
                          setAuthor('Coach Cedar');
                        }
                      }}
                      style={inputStyle}
                    >
                      <option value="">Coach Cedar (default)</option>
                      {authors.map(a => (
                        <option key={a.id} value={a.id}>{a.name}</option>
                      ))}
                    </select>
                  ) : (
                    <input type="text" value={author} onChange={e => setAuthor(e.target.value)} style={inputStyle} placeholder="No authors yet — create one in Blog CMS" />
                  )}
                </FieldGroup>

                <FieldGroup label="Tags (comma-separated)">
                  <input type="text" value={tags} onChange={e => setTags(e.target.value)} style={inputStyle} placeholder="fitness, training, recovery" />
                </FieldGroup>

                <Divider />

                {/* Hero Image */}
                <FieldGroup label="Hero Image">
                  {heroImageUrl ? (
                    <div style={{ position: 'relative', marginBottom: '8px' }}>
                      <img src={heroImageUrl} alt="Hero" style={{ width: '100%', borderRadius: '6px', maxHeight: '160px', objectFit: 'cover' }} />
                      <button
                        onClick={() => setHeroImageUrl('')}
                        style={{ position: 'absolute', top: '4px', right: '4px', padding: '4px', background: 'rgba(0,0,0,0.5)', borderRadius: '50%', border: 'none', cursor: 'pointer' }}
                      >
                        <X size={12} style={{ color: 'white' }} />
                      </button>
                    </div>
                  ) : (
                    <label style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                      padding: '20px', border: '2px dashed #d1d5db', borderRadius: '6px',
                      cursor: 'pointer', fontSize: '12px', color: '#6b7280',
                    }}>
                      <Upload size={14} /> Upload hero image
                      <input
                        type="file" accept="image/*" style={{ display: 'none' }}
                        onChange={e => { const f = e.target.files?.[0]; if (f) handleHeroUpload(f); }}
                      />
                    </label>
                  )}
                </FieldGroup>

                <FieldGroup label={<>Excerpt <InfoTooltip tip={SOCIAL_FIELD_TIPS.excerpt.tip} /></>}>
                  <textarea
                    value={excerpt} onChange={e => setExcerpt(e.target.value)}
                    rows={3} placeholder="Brief description for post cards..."
                    style={{ ...inputStyle, resize: 'vertical' }}
                  />
                </FieldGroup>

                <Divider />

                {/* SEO */}
                <h4 style={{ fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>SEO Settings</h4>

                <FieldGroup label={<>SEO Title <InfoTooltip tip={SOCIAL_FIELD_TIPS.seoTitle.tip} charLimit={SOCIAL_FIELD_TIPS.seoTitle.charLimit} /></>}>
                  <input type="text" value={seoTitle} onChange={e => setSeoTitle(e.target.value)} style={inputStyle} placeholder={title} />
                  <CharCount value={seoTitle || title} max={60} />
                </FieldGroup>

                <FieldGroup label={<>Meta Description <InfoTooltip tip={SOCIAL_FIELD_TIPS.seoDescription.tip} charLimit={SOCIAL_FIELD_TIPS.seoDescription.charLimit} /></>}>
                  <textarea
                    value={seoDescription} onChange={e => setSeoDescription(e.target.value)}
                    rows={2} style={{ ...inputStyle, resize: 'vertical' }}
                    placeholder={excerpt}
                  />
                  <CharCount value={seoDescription || excerpt} max={155} />
                </FieldGroup>

                {/* Noindex toggle */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                  <input
                    type="checkbox" checked={noindex}
                    onChange={e => setNoindex(e.target.checked)}
                    style={{ accentColor: '#dc2626', cursor: 'pointer' }}
                    id="noindex-toggle"
                  />
                  <label htmlFor="noindex-toggle" style={{ fontSize: '11px', fontWeight: 600, color: noindex ? '#dc2626' : '#6b7280', cursor: 'pointer' }}>
                    Noindex
                    <InfoTooltip tip="When enabled, this post will be hidden from search engines and AI crawlers. Use for internal-only content, drafts shared by link, or test posts you don't want indexed." />
                  </label>
                  {noindex && (
                    <span style={{ fontSize: '10px', color: '#dc2626', fontWeight: 500 }}>
                      Search engines will ignore this post
                    </span>
                  )}
                </div>

                <Divider />

                {/* Social Sharing */}
                <h4 style={{ fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>Social Sharing</h4>
                <p style={{ fontSize: '10px', color: '#9ca3af', marginBottom: '12px', lineHeight: 1.4 }}>
                  Controls how link previews appear on LinkedIn, Facebook, X, etc. Hover the <span style={{ color: '#6b7280' }}>(i)</span> icons for content guidance.
                </p>

                {/* Live Social Preview */}
                <div style={{ marginBottom: '16px' }}>
                  <SocialPreviews data={{
                    title, socialTitle, excerpt, socialDescription,
                    socialImageUrl, heroImageUrl: heroImageUrl, slug, author,
                    twitterCardType, twitterTitle, twitterDescription, twitterImageUrl,
                    pinterestImageUrl,
                  }} />
                </div>

                <Divider />

                <FieldGroup label={<>Social Share Title <InfoTooltip tip={SOCIAL_FIELD_TIPS.socialTitle.tip} charLimit={SOCIAL_FIELD_TIPS.socialTitle.charLimit} /></>}>
                  <input
                    type="text" value={socialTitle} onChange={e => setSocialTitle(e.target.value)}
                    style={inputStyle} placeholder={title} maxLength={70}
                  />
                  <CharCount value={socialTitle || title} max={70} />
                </FieldGroup>

                <FieldGroup label={<>Social Share Description <InfoTooltip tip={SOCIAL_FIELD_TIPS.socialDescription.tip} charLimit={SOCIAL_FIELD_TIPS.socialDescription.charLimit} /></>}>
                  <textarea
                    value={socialDescription} onChange={e => setSocialDescription(e.target.value)}
                    rows={2} style={{ ...inputStyle, resize: 'vertical' }}
                    placeholder={excerpt} maxLength={155}
                  />
                  <CharCount value={socialDescription || excerpt} max={155} />
                </FieldGroup>

                <FieldGroup label={<>Social Share Image (1200 x 630) <InfoTooltip tip={SOCIAL_FIELD_TIPS.socialImage.tip} charLimit={SOCIAL_FIELD_TIPS.socialImage.charLimit} /></>}>
                  {socialImageUrl ? (
                    <div style={{ position: 'relative', marginBottom: '8px' }}>
                      <img src={socialImageUrl} alt="OG" style={{ width: '100%', borderRadius: '6px', maxHeight: '120px', objectFit: 'cover' }} />
                      <button
                        onClick={() => setSocialImageUrl('')}
                        style={{ position: 'absolute', top: '4px', right: '4px', padding: '4px', background: 'rgba(0,0,0,0.5)', borderRadius: '50%', border: 'none', cursor: 'pointer' }}
                      >
                        <X size={12} style={{ color: 'white' }} />
                      </button>
                    </div>
                  ) : (
                    <label style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                      padding: '14px', border: '2px dashed #d1d5db', borderRadius: '6px',
                      cursor: 'pointer', fontSize: '11px', color: '#6b7280',
                    }}>
                      <Upload size={12} /> Upload 1200x630 image
                      <input type="file" accept="image/jpeg,image/png" style={{ display: 'none' }}
                        onChange={e => { const f = e.target.files?.[0]; if (f) handleSocialImageUpload(f, setSocialImageUrl); }}
                      />
                    </label>
                  )}
                </FieldGroup>

                <FieldGroup label={<>Image Alt Text <InfoTooltip tip={SOCIAL_FIELD_TIPS.socialImageAlt.tip} charLimit={SOCIAL_FIELD_TIPS.socialImageAlt.charLimit} /></>}>
                  <input
                    type="text" value={socialImageAlt} onChange={e => setSocialImageAlt(e.target.value)}
                    style={inputStyle} placeholder="Describe the share image" maxLength={125}
                  />
                </FieldGroup>

                <FieldGroup label={<>Canonical URL Override <InfoTooltip tip={SOCIAL_FIELD_TIPS.canonicalUrl.tip} /></>}>
                  <input
                    type="text" value={canonicalUrlOverride} onChange={e => setCanonicalUrlOverride(e.target.value)}
                    style={inputStyle} placeholder={`https://coachcedar.com/blog/${slug}`}
                  />
                </FieldGroup>

                {/* X / Twitter Overrides */}
                <details style={{ marginBottom: '12px' }}>
                  <summary style={{ fontSize: '11px', fontWeight: 600, color: '#6b7280', cursor: 'pointer', marginBottom: '8px' }}>
                    X / Twitter Overrides
                  </summary>
                  <div style={{ paddingLeft: '4px' }}>
                    <FieldGroup label={<>X Card Type <InfoTooltip tip={SOCIAL_FIELD_TIPS.xCardType.tip} /></>}>
                      <select value={twitterCardType} onChange={e => setTwitterCardType(e.target.value)} style={inputStyle}>
                        <option value="summary_large_image">Large Image (recommended)</option>
                        <option value="summary">Summary (small thumbnail)</option>
                      </select>
                    </FieldGroup>
                    <FieldGroup label={<>X Title <InfoTooltip tip={SOCIAL_FIELD_TIPS.xTitle.tip} charLimit={SOCIAL_FIELD_TIPS.xTitle.charLimit} /></>}>
                      <input type="text" value={twitterTitle} onChange={e => setTwitterTitle(e.target.value)} style={inputStyle} placeholder="Falls back to Social Share Title" maxLength={70} />
                    </FieldGroup>
                    <FieldGroup label={<>X Description <InfoTooltip tip={SOCIAL_FIELD_TIPS.xDescription.tip} charLimit={SOCIAL_FIELD_TIPS.xDescription.charLimit} /></>}>
                      <textarea value={twitterDescription} onChange={e => setTwitterDescription(e.target.value)} rows={2} style={{ ...inputStyle, resize: 'vertical' }} placeholder="Falls back to Social Share Description" maxLength={125} />
                    </FieldGroup>
                    <FieldGroup label={<>X Image Override <InfoTooltip tip={SOCIAL_FIELD_TIPS.xImage.tip} charLimit={SOCIAL_FIELD_TIPS.xImage.charLimit} /></>}>
                      {twitterImageUrl ? (
                        <div style={{ position: 'relative', marginBottom: '8px' }}>
                          <img src={twitterImageUrl} alt="X" style={{ width: '100%', borderRadius: '6px', maxHeight: '100px', objectFit: 'cover' }} />
                          <button onClick={() => setTwitterImageUrl('')} style={{ position: 'absolute', top: '4px', right: '4px', padding: '4px', background: 'rgba(0,0,0,0.5)', borderRadius: '50%', border: 'none', cursor: 'pointer' }}><X size={12} style={{ color: 'white' }} /></button>
                        </div>
                      ) : (
                        <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', padding: '10px', border: '2px dashed #d1d5db', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', color: '#6b7280' }}>
                          <Upload size={12} /> Upload X image
                          <input type="file" accept="image/jpeg,image/png" style={{ display: 'none' }} onChange={e => { const f = e.target.files?.[0]; if (f) handleSocialImageUpload(f, setTwitterImageUrl); }} />
                        </label>
                      )}
                    </FieldGroup>
                    <FieldGroup label={<>X Image Alt <InfoTooltip tip={SOCIAL_FIELD_TIPS.xImageAlt.tip} charLimit={SOCIAL_FIELD_TIPS.xImageAlt.charLimit} /></>}>
                      <input type="text" value={twitterImageAlt} onChange={e => setTwitterImageAlt(e.target.value)} style={inputStyle} placeholder="Falls back to Image Alt Text" maxLength={125} />
                    </FieldGroup>
                  </div>
                </details>

                {/* Pinterest Override */}
                <details style={{ marginBottom: '12px' }}>
                  <summary style={{ fontSize: '11px', fontWeight: 600, color: '#6b7280', cursor: 'pointer', marginBottom: '8px' }}>
                    Pinterest Override
                  </summary>
                  <div style={{ paddingLeft: '4px' }}>
                    <FieldGroup label={<>Pinterest Image (1000 x 1500) <InfoTooltip tip={SOCIAL_FIELD_TIPS.pinterestImage.tip} charLimit={SOCIAL_FIELD_TIPS.pinterestImage.charLimit} /></>}>
                      {pinterestImageUrl ? (
                        <div style={{ position: 'relative', marginBottom: '8px' }}>
                          <img src={pinterestImageUrl} alt="Pinterest" style={{ width: '60%', borderRadius: '6px', maxHeight: '140px', objectFit: 'cover' }} />
                          <button onClick={() => setPinterestImageUrl('')} style={{ position: 'absolute', top: '4px', left: 'calc(60% - 24px)', padding: '4px', background: 'rgba(0,0,0,0.5)', borderRadius: '50%', border: 'none', cursor: 'pointer' }}><X size={12} style={{ color: 'white' }} /></button>
                        </div>
                      ) : (
                        <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', padding: '10px', border: '2px dashed #d1d5db', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', color: '#6b7280' }}>
                          <Upload size={12} /> Upload vertical pin (1000x1500)
                          <input type="file" accept="image/jpeg,image/png" style={{ display: 'none' }} onChange={e => { const f = e.target.files?.[0]; if (f) handleSocialImageUpload(f, setPinterestImageUrl); }} />
                        </label>
                      )}
                    </FieldGroup>
                  </div>
                </details>

                {/* Cache busting note */}
                <div style={{ padding: '10px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '6px', marginBottom: '12px' }}>
                  <p style={{ fontSize: '10px', color: '#92400e', lineHeight: 1.5, margin: 0 }}>
                    After publishing, platforms cache link previews. Force refresh:
                    {' '}<a href="https://www.linkedin.com/post-inspector/" target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb' }}>LinkedIn</a>
                    {' | '}<a href="https://developers.facebook.com/tools/debug/" target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb' }}>Facebook</a>
                    {' | '}<a href="https://cards-dev.twitter.com/validator" target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb' }}>X</a>
                  </p>
                </div>

                {/* Revision history */}
                {post && (
                  <>
                    <Divider />
                    <button
                      onClick={async () => {
                        try {
                          const revisions = await AdminBlogService.listRevisions(post.id);
                          alert(`${revisions.length} revision(s) saved. Revision management coming soon.`);
                        } catch (err) {
                          console.error(err);
                        }
                      }}
                      style={{
                        width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                        padding: '8px', fontSize: '13px', color: '#6b7280',
                        background: 'none', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer',
                      }}
                    >
                      <History size={14} /> Revision History
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══ PREVIEW MODE ══ */}
      {mode === 'preview' && (
        <BlogPreview
          title={title}
          author={author}
          category={category}
          heroImageUrl={heroImageUrl}
          excerpt={excerpt}
          readTime=""
          tags={tags.split(',').map(t => t.trim()).filter(Boolean)}
          contentHtml={editor?.getHTML() || ''}
          slug={slug}
        />
      )}

      {/* Publish Validation Checklist Modal */}
      {showPublishChecklist && (
        <PublishChecklist
          postData={getChecklistPostData()}
          authors={authors.map(a => ({ id: a.id, bio: a.bio }))}
          onPublish={handlePublish}
          onClose={() => setShowPublishChecklist(false)}
          isPublished={status === 'published'}
        />
      )}

      {/* Media Library Modal */}
      {showMediaLibrary && (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.4)' }}
          onClick={() => setShowMediaLibrary(false)}
        >
          <div
            style={{ background: 'white', borderRadius: '12px', padding: '24px', maxWidth: '700px', width: '100%', maxHeight: '80vh', overflow: 'auto' }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h2 style={{ fontSize: '18px', fontWeight: 700 }}>Media Library</h2>
              <button onClick={() => setShowMediaLibrary(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <X size={20} style={{ color: '#6b7280' }} />
              </button>
            </div>

            {/* Upload new */}
            <label style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
              padding: '16px', border: '2px dashed #d1d5db', borderRadius: '8px',
              cursor: 'pointer', fontSize: '13px', color: '#6b7280', marginBottom: '16px',
            }}>
              <Upload size={16} /> Upload new image
              <input type="file" accept="image/*,video/*" style={{ display: 'none' }}
                onChange={async e => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  setUploading(true);
                  try {
                    await AdminBlogService.uploadMedia(file);
                    const items = await AdminBlogService.listMedia();
                    setMediaItems(items);
                  } catch (err) {
                    console.error(err);
                  } finally {
                    setUploading(false);
                  }
                }}
              />
            </label>

            {uploading && <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>Uploading...</p>}

            {/* Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
              {mediaItems.map(m => (
                <div
                  key={m.id}
                  onClick={() => handleMediaLibrarySelect(m)}
                  style={{
                    aspectRatio: '1', borderRadius: '6px', overflow: 'hidden',
                    border: '1px solid #e5e7eb', cursor: 'pointer', background: '#f3f4f6',
                  }}
                >
                  {m.content_type?.startsWith('image') ? (
                    <img src={m.url} alt={m.alt_text || ''} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', fontSize: '11px', color: '#9ca3af' }}>
                      {m.original_filename}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {mediaItems.length === 0 && !uploading && (
              <p style={{ textAlign: 'center', padding: '24px', color: '#9ca3af', fontSize: '13px' }}>No media uploaded yet.</p>
            )}
          </div>
        </div>
      )}

      {/* Editor styles */}
      <style>{`
        .blog-editor-content h2 { font-size: 24px; font-weight: 700; margin: 24px 0 8px; }
        .blog-editor-content h3 { font-size: 20px; font-weight: 600; margin: 20px 0 6px; }
        .blog-editor-content h4 { font-size: 17px; font-weight: 600; margin: 16px 0 4px; }
        .blog-editor-content p { margin: 0 0 12px; }
        .blog-editor-content ul, .blog-editor-content ol { padding-left: 24px; margin: 8px 0; }
        .blog-editor-content blockquote { border-left: 3px solid #2E5E34; padding-left: 16px; margin: 16px 0; color: #4b5563; font-style: italic; }
        .blog-editor-content hr { border: none; border-top: 1px solid #e5e7eb; margin: 24px 0; }
        .blog-editor-content a { color: #2E5E34; text-decoration: underline; }
        .blog-editor-content img { max-width: 100%; border-radius: 8px; margin: 16px 0; }
        .blog-editor-content iframe { max-width: 100%; border-radius: 8px; margin: 16px 0; }
        .blog-editor-content .ProseMirror-focused { outline: none; }
        .blog-editor-content p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          float: left; color: #adb5bd; pointer-events: none; height: 0;
        }
      `}</style>
    </div>
  );
};

// ── Helper components ──

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '6px 10px', fontSize: '13px',
  border: '1px solid #d1d5db', borderRadius: '4px', outline: 'none',
};

const FieldGroup: React.FC<{ label: React.ReactNode; children: React.ReactNode }> = ({ label, children }) => (
  <div style={{ marginBottom: '12px' }}>
    <label style={{ fontSize: '11px', fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: '4px' }}>
      {label}
    </label>
    {children}
  </div>
);

const Divider: React.FC = () => (
  <hr style={{ border: 'none', borderTop: '1px solid #e5e7eb', margin: '16px 0' }} />
);

const ToolbarButton: React.FC<{
  onClick: () => void;
  icon: React.ReactNode;
  title: string;
  active?: boolean;
}> = ({ onClick, icon, title, active }) => (
  <button
    onClick={onClick}
    title={title}
    style={{
      padding: '6px', borderRadius: '4px', border: 'none', cursor: 'pointer',
      background: active ? '#e5e7eb' : 'transparent',
      color: active ? '#111827' : '#6b7280',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}
  >
    {icon}
  </button>
);

const CharCount: React.FC<{ value: string; max: number }> = ({ value, max }) => {
  const len = value.length;
  const over = len > max;
  return (
    <span style={{ fontSize: '10px', color: over ? '#dc2626' : '#9ca3af', marginTop: '2px', display: 'block' }}>
      {len}/{max}{over ? ' — will be truncated' : ''}
    </span>
  );
};

const ToolbarSep: React.FC = () => (
  <div style={{ width: '1px', height: '20px', background: '#d1d5db', margin: '4px 4px', alignSelf: 'center' }} />
);

/** Live content stats bar — word count, headings, links, images */
const ContentStatsBar: React.FC<{ html: string }> = ({ html }) => {
  const stats = useMemo(() => {
    const div = document.createElement('div');
    div.innerHTML = html;
    const text = div.textContent || '';
    const words = text.trim() ? text.trim().split(/\s+/).length : 0;
    const headings = div.querySelectorAll('h2, h3, h4').length;
    const allLinks = div.querySelectorAll('a[href]');
    let internal = 0;
    let external = 0;
    allLinks.forEach(a => {
      const href = a.getAttribute('href') || '';
      if (href.includes('coachcedar.com') || href.startsWith('/')) internal++;
      else if (href.startsWith('http')) external++;
    });
    const images = div.querySelectorAll('img').length;
    const imagesNoAlt = Array.from(div.querySelectorAll('img')).filter(i => !(i.getAttribute('alt')?.trim())).length;
    return { words, headings, internal, external, images, imagesNoAlt };
  }, [html]);

  const wordColor = stats.words >= 1500 ? '#166534' : stats.words >= 800 ? '#2E5E34' : stats.words >= 400 ? '#d97706' : '#dc2626';

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '16px', padding: '6px 12px',
      background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '6px',
      marginTop: '8px', fontSize: '11px', color: '#6b7280',
    }}>
      <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 600, color: wordColor }}>
        <Type size={12} /> {stats.words.toLocaleString()} words
        {stats.words < 800 && <span style={{ fontWeight: 400, color: '#d97706' }}>(min 800)</span>}
        {stats.words >= 1500 && <span style={{ fontWeight: 400 }}>(pillar)</span>}
      </span>
      <span>{stats.headings} heading{stats.headings !== 1 ? 's' : ''}</span>
      <span>{stats.internal} internal / {stats.external} external link{stats.internal + stats.external !== 1 ? 's' : ''}</span>
      {stats.images > 0 && (
        <span style={{ color: stats.imagesNoAlt > 0 ? '#d97706' : '#6b7280' }}>
          {stats.images} image{stats.images !== 1 ? 's' : ''}
          {stats.imagesNoAlt > 0 && ` (${stats.imagesNoAlt} missing alt)`}
        </span>
      )}
    </div>
  );
};
